import { Form, Head } from "@inertiajs/react"
import { useState } from "react"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsBrokerProfilePath } from "@/routes"
import type { BreadcrumbItem, BrokerProfile } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Branding", href: settingsBrokerProfilePath() },
]

// Treystar defaults — mirror BrokerProfile::DEFAULT_*_COLOR.
const DEFAULT_PRIMARY_COLOR = "#0f1f3a"
const DEFAULT_ACCENT_COLOR = "#b8860b"

interface Props {
  broker_profile: BrokerProfile
}

function ColorField({
  name,
  label,
  hint,
  defaultValue,
}: {
  name: string
  label: string
  hint: string
  defaultValue: string
}) {
  const [value, setValue] = useState(defaultValue)
  const isHex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)

  return (
    <div className="grid gap-2">
      <Label htmlFor={`${name}-hex`}>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`${label} swatch`}
          value={isHex ? value : "#000000"}
          onChange={(e) => setValue(e.target.value)}
          className="h-9 w-12 shrink-0 cursor-pointer rounded-md border bg-transparent p-1"
        />
        <Input
          id={`${name}-hex`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={defaultValue}
          className="max-w-[140px] font-mono"
        />
      </div>
      <input type="hidden" name={name} value={value} />
      <p className="text-muted-foreground text-xs">{hint}</p>
    </div>
  )
}

export default function Show({ broker_profile }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Branding" />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Broker Branding"
            description="Add your company details, logo, and colors. These appear on co-branded brochures."
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Branding Details</CardTitle>
            </CardHeader>
            <CardContent>
              <Form
                method="patch"
                action={settingsBrokerProfilePath()}
                encType="multipart/form-data"
                options={{ preserveScroll: true }}
                className="space-y-4"
              >
                {({ errors, processing, recentlySuccessful }) => (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor="company_name">Company Name</Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        defaultValue={broker_profile.company_name ?? ""}
                        placeholder="e.g. NAI Realty Group"
                      />
                      <InputError messages={errors.company_name} />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        defaultValue={broker_profile.phone ?? ""}
                        placeholder="e.g. (555) 123-4567"
                      />
                      <InputError messages={errors.phone} />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        name="website"
                        type="url"
                        defaultValue={broker_profile.website ?? ""}
                        placeholder="https://yourcompany.com"
                      />
                      <InputError messages={errors.website} />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="logo">Company Logo</Label>
                      {broker_profile.logo_url && (
                        <img
                          src={broker_profile.logo_url}
                          alt="Current logo"
                          className="mb-2 h-12 w-auto object-contain"
                        />
                      )}
                      <Input id="logo" name="logo" type="file" accept="image/*" />
                      <InputError messages={errors.logo} />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <ColorField
                          name="brand_primary_color"
                          label="Primary Color"
                          hint="Headings and bars. Leave as-is to use Treystar navy."
                          defaultValue={broker_profile.brand_primary_color ?? DEFAULT_PRIMARY_COLOR}
                        />
                        <InputError messages={errors.brand_primary_color} />
                      </div>
                      <div>
                        <ColorField
                          name="brand_accent_color"
                          label="Accent Color"
                          hint="Rules and highlights. Leave as-is to use Treystar gold."
                          defaultValue={broker_profile.brand_accent_color ?? DEFAULT_ACCENT_COLOR}
                        />
                        <InputError messages={errors.brand_accent_color} />
                      </div>
                    </div>

                    <div className="flex items-center gap-4 pt-2">
                      <Button type="submit" disabled={processing}>Save Branding</Button>
                      <p className={`text-sm text-neutral-600 transition ease-in-out ${recentlySuccessful ? "opacity-100" : "opacity-0"}`}>
                        Saved
                      </p>
                    </div>
                  </>
                )}
              </Form>
            </CardContent>
          </Card>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
